import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createInjection, fetchInjections } from "../../store/slices/injectionsSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import InjectionForm from "./InjectionForm";

const AddInjection = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const isLoading = useSelector((state) => state.injections.isLoading);

    const [formData, setFormData] = useState({
        patientId, // initialize
        caseId,    // initialize
        // patientType: "Private Patient",
        // STANDARD INFORMATION 
        standardName: "",
        issueDate: "",
        reviewDate: "",
        documentNumber: "",
        revisionNumber: "",
        departmentName: "",
        // PATIENT INFORMATION
        patientName: "",
        patientGender: "",
        patientDOB: "",
        patientContact: "",

        // OnDutyForm fields
        quetiapine: "No",
        serenace: "No",
        vallium: "No",
        avil: "No",
        clopixol: "No",
        dormicum: "No",
        procyclidine: "No",
        doctorRemarks: "",
        doctorName: "",
        doctorDesignation: "",
        doctorPlace: "",

        // AssignedRnForm fields
        rnQuetiapine: "No",
        rnSerenace: "No",
        rnVallium: "No",
        rnAvil: "No",
        rnClopixol: "No",
        rnDormicum: "No",
        rnProcyclidine: "No",
        rnRemarks: "",
        rnName: "",
        rnDesignation: "",
        rnPlace: "",
        amount: "",
        paymentStatus: "",
        referenceNo: "",
        paymentDate: "",
    });

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        const parsedDob = patient.dob
                            ? (patient.dob.includes('T') ? patient.dob.split('T')[0] : patient.dob.split(' ')[0])
                            : "";
                        setFormData((prev) => ({
                            ...prev,
                            patientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            patientGender: patient.gender || "",
                            patientDOB: parsedDob,
                            patientContact: patient.phone || "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient:", err));
        }
    }, [dispatch, patientId]);

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.patientName) newErrors.patientName = "Patient name is required";
        if (!formData.doctorName) newErrors.doctorName = "Doctor name is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nullifyIfNo = (value, fields) => {
        if (value === "No") {
            const updated = {};
            fields.forEach(f => updated[f] = null);
            return updated;
        }
        return {};
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        // Auto-null fields when drug is "No"
        const onDutyFix = {
            ...nullifyIfNo(formData.quetiapine, ["quetiapineDose"]),
            ...nullifyIfNo(formData.serenace, ["serenaceDose", "serenaceType"]),
            ...nullifyIfNo(formData.vallium, ["valliumDose", "valliumType"]),
            ...nullifyIfNo(formData.avil, ["avilDose", "avilType"]),
            ...nullifyIfNo(formData.clopixol, ["clopixolDose", "clopixolType"]),
            ...nullifyIfNo(formData.dormicum, ["dormicumDose", "dormicumType"]),
            ...nullifyIfNo(formData.procyclidine, ["procyclidineDose", "procyclidineType"]),
        };

        const rnFix = {
            ...nullifyIfNo(formData.rnSerenace, ["rnSerenaceDose", "rnSerenaceType"]),
            ...nullifyIfNo(formData.rnVallium, ["rnValliumDose", "rnValliumType"]),
            ...nullifyIfNo(formData.rnAvil, ["rnAvilDose", "rnAvilType"]),
            ...nullifyIfNo(formData.rnClopixol, ["rnClopixolDose", "rnClopixolType"]),
            ...nullifyIfNo(formData.rnDormicum, ["rnDormicumDose", "rnDormicumType"]),
            ...nullifyIfNo(formData.rnProcyclidine, ["rnProcyclidineDose", "rnProcyclidineType"]),
            ...nullifyIfNo(formData.rnQuetiapine, ["rnQuetiapineDose"]),
        };

        // Apply null-field cleanup
        Object.assign(formData, onDutyFix, rnFix);

        // Map the formData to backend payload
        const payload = {
            patient_id: formData.patientId || null,
            case_id: formData.caseId || null,
            // patient_type: formData.patientType,.
            department_name: formData.departmentName,
            revision_no: formData.revisionNumber,
            document_no: formData.documentNumber,
            review_date: formData.reviewDate,
            issue_date: formData.issueDate,
            standard_name: formData.standardName,
            name: formData.patientName,
            gender: formData.patientGender ? formData.patientGender.charAt(0).toUpperCase() + formData.patientGender.slice(1).toLowerCase() : null,
            dob: formData.patientDOB,
            contact: formData.patientContact,
            amount: formData.amount,
            payment_status: formData.paymentStatus,
            reference_no: formData.referenceNo,
            payment_date: formData.paymentDate,

            on_duty: {

                quetiapine: formData.quetiapine,
                quetiapine_dose: formData.quetiapineDose || null,

                serenace: formData.serenace,
                serenace_dose: formData.serenaceDose || null,
                serenace_type: formData.serenaceType || null,

                vallium: formData.vallium,
                vallium_dose: formData.valliumDose || null,
                vallium_type: formData.valliumType || null,

                avil: formData.avil,
                avil_dose: formData.avilDose || null,
                avil_type: formData.avilType || null,

                clopixol: formData.clopixol,
                clopixol_dose: formData.clopixolDose || null,
                clopixol_type: formData.clopixolType || null,

                dormicum: formData.dormicum,
                dormicum_dose: formData.dormicumDose || null,
                dormicum_type: formData.dormicumType || null,

                procyclidine: formData.procyclidine,
                procyclidine_dose: formData.procyclidineDose || null,
                procyclidine_type: formData.procyclidineType || null,

                doctor_remarks: formData.doctorRemarks || null,
                doctor_name: formData.doctorName || null,
                doctor_designation: formData.doctorDesignation || null,
                doctor_place: formData.doctorPlace || null,
            },

            rn: {
                rn_quetiapine: formData.quetiapine,
                rn_quetiapine_dose: formData.quetiapineDose || null,

                rn_serenace: formData.rnSerenace,
                rn_serenace_dose: formData.rnSerenaceDose || null,
                rn_serenace_type: formData.rnSerenaceType || null,

                rn_vallium: formData.rnVallium,
                rn_vallium_dose: formData.rnValliumDose || null,
                rn_vallium_type: formData.rnValliumType || null,

                rn_avil: formData.rnAvil,
                rn_avil_dose: formData.rnAvilDose || null,
                rn_avil_type: formData.rnAvilType || null,

                rn_clopixol: formData.rnClopixol,
                rn_clopixol_dose: formData.rnClopixolDose || null,
                rn_clopixol_type: formData.rnClopixolType || null,

                rn_dormicum: formData.rnDormicum,
                rn_dormicum_dose: formData.rnDormicumDose || null,
                rn_dormicum_type: formData.rnDormicumType || null,

                rn_procyclidine: formData.rnProcyclidine,
                rn_procyclidine_dose: formData.rnProcyclidineDose || null,
                rn_procyclidine_type: formData.rnProcyclidineType || null,

                rn_remarks: formData.rnRemarks || null,
                rn_name: formData.rnName || null,
                rn_designation: formData.rnDesignation || null,
                rn_place: formData.rnPlace || null,
            },
        };

        try {
            const res = await dispatch(createInjection(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Injection record added successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            });

            dispatch(fetchInjections()); // refresh injections list
            navigate(`/injections/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            console.error(err);
            Swal.fire({
                title: "Error",
                text: err?.message || "Failed to add injection record.",
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <InjectionForm
                                formData={formData}
                                handleChange={handleChange}
                                isLoading={isLoading}
                            />
                            <div className="row">
                                <div className="col-md-2 ms-auto">
                                    <button type="submit" className="btn btn-primary w-100">
                                        <i className="ri-save-3-line me-1"></i> Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddInjection;
